<template>
  <div class="settings-indicators">
    <IndicatorsSettings :themes="themes" @update="updateThemes" />
    <ExpandableSettingsItem :title="$t('message.IW_TITLE')" class="indic-warnings">
      <ExpandableSettingsItem v-for="theme in themes" :key="theme.id" :title="theme.name">
        <ExpandableSettingsItem v-for="indicator in theme.indicators" :key="indicator.id" :title="indicator.name">
          <div v-if="!indicator.warningMessage">
            <div class="mt10em">{{ $t('message.IW_NO_MSG_SET') }}</div>
            <button
              class="generic-border generic-border--text-btn generic-border--gray-border v-centerer mt10"
              @click="createWarning(indicator)"
            >
              <div class="generic-border__left">
                <Icon class="generic-border__left-blue-icon">{{ addExtraBold }}</Icon>
                <span>{{ $t("message.IW_SET_A_MSG") }}</span>
              </div>
              <Icon class="generic-border__right-arrow">{{ arrowDownLine }}</Icon>
            </button>
          </div>
          <div v-else>
            <EditableLabel
              v-model="indicator.warningMessage.message"
              :placeholder="$t('message.IW_MSG_PH')"
              use-textarea
              :legend="$t('message.IW_CUSTOM_MESSAGE')"
              class="indic-warnings__message"
              @input="warningEdited(indicator.warningMessage)"
            />
            <div class="align-middle mt10">
              <label class="legend" :for="`overrun${indicator.id}`">
                {{ $t('message.IW_ALLOWED_OVERRUN') + $t('message.TWO_DOTS') }}
              </label>
              <InputNumber
                :id="`overrun${indicator.id}`"
                v-model="indicator.warningMessage.gap"
                class="ml5"
                @input="inputField(indicator.warningMessage)"
                @blur="warningEdited(indicator.warningMessage)"
              />
            </div>
            <div class="align-middle mt10">
              <label class="legend" :for="`ndays${indicator.id}`">
                {{ $t('message.IW_N_DAYS') + $t('message.TWO_DOTS') }}
              </label>
              <InputNumber
                :id="`ndays${indicator.id}`"
                v-model="indicator.warningMessage.nDays"
                class="ml5"
                @input="inputField(indicator.warningMessage)"
                @blur="warningEdited(indicator.warningMessage)"
              />
            </div>
            <button
              class="generic-border generic-border--text-btn generic-border--red"
              @click.stop="deleteWarning(indicator)"
            >
              <Icon>{{ deleteMedium }}</Icon>
              <span>{{ $t('message.DELETE') }}</span>
            </button>
          </div>
        </ExpandableSettingsItem>
      </ExpandableSettingsItem>
    </ExpandableSettingsItem>
    <ExpandableSettingsItem :title="$t('message.INDIC_INPUT_DEFAULT_DATE')">
      <div>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div class="field-hint mb5" v-html="$t('message.INDIC_INPUT_DEFAULT_HINT')" />
        <InputNumber
          :value="currentTeam.indicatorInputDaysGap"
          :legend="$t('message.NB_OF_DAYS') + $t('message.TWO_DOTS')"
          @input="save('indicatorInputDaysGap', $event)"
        />
      </div>
    </ExpandableSettingsItem>
    <ExpandableSettingsItem :title="$t('message.INDIC_SETTINGS_CHARTS_DISPLAY')">
      <div>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div class="field-hint mb5" v-html="$t('message.INDIC_SETTINGS_DISPLAY_LABELS_HINT')" />
        <div class="legend mb5">
          {{ `${$t('message.INDIC_SETTINGS_DISPLAY_LABELS')}${$t('message.TWO_DOTS')}` }}
        </div>
        <RadioGroup
          :value="currentTeam.displayIndicatorLabels"
          :items="labelsOptionItems"
          @input="save('displayIndicatorLabels', $event, true)"
        />
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div class="field-hint mb5 mt10" v-html="$t('message.INDIC_SETTINGS_DISPLAY_VALUES_HINT')" />
        <IntCheckbox
          :value="currentTeam.displayIndicatorValues"
          :title="$t('message.INDIC_SETTINGS_DISPLAY_VALUES')"
          @input="save('displayIndicatorValues', $event)"
        />
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div class="field-hint mb5 mt10" v-html="$t('message.INDIC_SETTINGS_DISPLAY_PROBLEMS_IMPACT_HINT')" />
        <IntCheckbox
          :value="currentTeam.displayProblemsInIndicatorCharts"
          :title="$t('message.INDIC_SETTINGS_DISPLAY_PROBLEMS_IMPACT')"
          @input="save('displayProblemsInIndicatorCharts', $event)"
        />
      </div>
    </ExpandableSettingsItem>
    <ExpandableSettingsItem :title="$t('message.DISPLAY_NEO_HINTS')">
      <div>
        <IntCheckbox
          :value="currentTeam.displayIndicatorHints"
          :title="$t('message.DISPLAY_NEO_HINTS')"
          @input="save('displayIndicatorHints', $event)"
        />
      </div>
    </ExpandableSettingsItem>
  </div>
</template>

<script>
/**
 * Indicators configuration page
 *
 * @vue-prop {Boolean} [admin=false] - If true, editing indicators from the admin config
 * @vue-event {Array} save - Emit indicator theme list, triggered before saving
 */

import toastr from 'toastr';
import { cloneDeep, isEqual } from 'lodash';
// Vuex
import { mapState } from 'vuex';
import { FETCH_INDICATOR_DISPLAY_CONFIGS, SET_TEAM_PARAM, FETCH_INDICATOR } from '@/store/action-types';
import { GET_INDICATOR_DISPLAY_CONFIGS, SET_TEAM_THEMES } from '@/store/mutation-types';
// Components
import EditableLabel from '@/components/editable-label.vue';
import ExpandableSettingsItem from '@/components/expandable-settings-item.vue';
import Icon from '@/components/icon.vue';
import IndicatorsSettings from '@/components/indicators-settings.vue';
import IntCheckbox from '@/components/int-checkbox.vue';
import InputNumber from '@/components/input-number.vue';
import RadioGroup from '@/components/radio-group.vue';
// Utils
import { calert } from '@/utils/main';
import { itemExpand } from '@/utils/expandable';
import {
  addExtraBold,
  arrowDownLine,
  deleteMedium,
} from '@/assets/icons/';

export default {

  name: 'SettingsIndicators',

  components: {
    EditableLabel,
    ExpandableSettingsItem,
    Icon,
    IndicatorsSettings,
    IntCheckbox,
    InputNumber,
    RadioGroup,
  },

  data() {
    return {
      addExtraBold,
      arrowDownLine,
      deleteMedium,
      themes: [],
      labelsOptionItems: [
        { id: 0, label: this.$t('message.INDIC_SETTINGS_NO_LABELS') },
        { id: 1, label: this.$t('message.INDIC_SETTINGS_DISPLAY_LABEL_DATE') },
        { id: 2, label: this.$t('message.INDIC_SETTINGS_DISPLAY_LABEL_CODE') },
      ],
      editionTimeout: null,
      iidgInputTimeout: null,
      // Properties used by other components. See src/views/readme.md
      pageTitle: this.$t('message.SETTINGS_INDICATORS'),
    };
  },

  computed: {
    /** Array: Ids of the teams accessible to the current team */
    accessibleTeamIds() {
      return [
        this.currentTeam.id,
        ...this.$store.getters.allAccessibleTeams.map(t => t.id),
      ];
    },
    // Custom indicator display configs
    ...mapState({
      customConfigs: state => state[GET_INDICATOR_DISPLAY_CONFIGS.stateKey] || [],
      customConfigsLoading: state => state[GET_INDICATOR_DISPLAY_CONFIGS.loadingKey],
    }),
  },

  watch: {
    accessibleTeamIds(newVal, oldVal) {
      if (!isEqual(newVal, oldVal)) {
        this.$store.dispatch(FETCH_INDICATOR, { teamIds: newVal });
      }
    },
  },

  created() {
    // Fetch indicators and themes
    this.App.post('/server/indicators/get.php', { includeIndicatorsGWusage: true }, (themes) => {
      if (!themes || !themes.length) {
        return;
      }
      themes.forEach((t) => {
        t.expanded = false;
        t.indicators.forEach((i) => {
          i.expanded = false;
        });
      });
      this.themes = themes;
      // Fetch warning messages
      // TODO: use vuex FETCH_INDICATOR_WARNINGS
      this.App.post('/server/indicators/warnings/get.php', {}, (warnings) => {
        warnings = warnings || {};
        this.linkWarningsToIndicators(warnings);
      });
    });

    // Fetch any custom configs
    if (!this.customConfigsLoading) {
      this.$store.dispatch(FETCH_INDICATOR_DISPLAY_CONFIGS);
    }

    // Fetch indicators of accessible teams for calculated indicators
    this.$store.dispatch(FETCH_INDICATOR, { teamIds: this.accessibleTeamIds });
  },

  methods: {
    itemExpand,
    /** Indicator input days gap was edited */
    save(param, value, noDelay = false) {
      // Timeout to prevent XHR on each input
      clearTimeout(this.iidgInputTimeout);
      this.iidgInputTimeout = setTimeout(() => {
        this.$store.dispatch(SET_TEAM_PARAM, { [param]: value });
      }, noDelay ? 0 : 500);
    },
    /** Create a default warning message for this indicator */
    createWarning(indicator) {
      if (!indicator.warningMessage) {
        this.$http.post('/server/indicators/warnings/create.php', { indicator: indicator.id })
          .then((response) => {
            if (typeof response.data === 'object') {
              indicator.warningMessage = response.data[indicator.id] || null;
            } else {
              toastr.error(this.$t('message.CONNECTION_ERROR'));
            }
          });
      }
    },
    inputField(warning) {
      clearTimeout(this.editionTimeout);
      this.editionTimeout = setTimeout(() => {
        this.warningEdited(warning);
      }, 700);
    },
    /** Warning messages edition */
    warningEdited(warning) {
      this.$http.post('/server/indicators/warnings/edit.php', warning).then(({ data }) => {
        if (data === 1) {
          toastr.success(this.$t('message.SAVED'));
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    deleteWarning(indicator) {
      calert(this.$t('message.IW_CONFIRM_DELETE'), [
        {
          title: this.$t('message.DELETE'),
          action: () => {
            this.deleteWarningConfirmed(indicator);
          },
          sClass: 'red',
        }, {
          title: this.$t('message.CANCEL'),
        },
      ]);
    },
    /** Unset a warning message from an indicator */
    deleteWarningConfirmed(indicator) {
      this.$http.post('/server/indicators/warnings/delete.php', { id: indicator.warningMessage.id })
        .then((response) => {
          if (typeof response.data === 'object') {
            this.linkWarningsToIndicators(response.data);
          } else {
            toastr.error(this.$t('message.CONNECTION_ERROR'));
          }
        });
    },
    /** Add a warningMessage attr to indicator objects */
    linkWarningsToIndicators(warnings) {
      for (let i = 0, len = this.themes.length, j, jlen; i < len; i++) {
        for (j = 0, jlen = this.themes[i].indicators.length; j < jlen; j++) {
          this.$set(this.themes[i].indicators[j], 'warningMessage', warnings[this.themes[i].indicators[j].id] || null);
        }
      }
    },
    /** Setup themes after an update */
    updateThemes(data) {
      this.$store.commit(SET_TEAM_THEMES, cloneDeep(data));
      toastr.remove();
      toastr.success(this.$t('message.INDICATORS_SAVED'));
      const tExpanded = {};
      const iExpanded = {};
      this.themes.forEach((t) => {
        tExpanded[`${t.id}`] = t.expanded;
        t.indicators.forEach((i) => {
          iExpanded[`${i.id}`] = i.expanded;
        });
      });
      data.forEach((t) => {
        t.expanded = !!tExpanded[`${t.id}`];
        t.indicators.forEach((i) => {
          i.expanded = !!iExpanded[`${i.id}`];
        });
      });
      this.themes = data;
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.settings-indicators {
  .indic-warnings {
    .sd-overflow {

      > div > .sd-overflow > div, &.sd-overflow > div {
        padding: 0 0 0 35px;
      }
      .settings-item {
        height: 35px;
        padding-left: 15px;
        border-bottom-left-radius: 50px;
        border-top-left-radius: 50px;
        border-left: 1px solid #82b5fa;

        img {
          height: 19px;
        }
      }
    }

    &__message {
      margin-top: .7rem;
    }

  }
}
</style>
